import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { registrationService, StepInfo, StepData, isSafetyStaffNotAssignedError } from 'shared/services/registrationService';
import { UserRole, RegistrationSource } from 'shared/types';
import { usePageTitle, getPageTitle } from 'shared/hooks/usePageTitle';
import { temporaryCodesApi } from 'shared/services/temporaryCodesApi';
import { useStrings } from 'shared/utils/stringService';

import { InvitationErrorScreen } from './components/InvitationErrorScreen';
import { InvitationLoadingScreen } from './components/InvitationLoadingScreen';
import { InvitationSuccessScreen } from './components/InvitationSuccessScreen';
import { RegistrationField } from './components/RegistrationField';
import { RegistrationSkeleton } from './components/RegistrationSkeleton';
// RegistrationProgressBar removed — invitation flow is single-screen.
import { MobileNavigationButtons } from './components/MobileNavigationButtons';
import { RefreshDetectedModal } from './components/RefreshDetectedModal';
import { SessionExpiredModal } from './components/SessionExpiredModal';

// Constants for step indices (0-based)
const STEP_INDICES = {
  PERSONAL_INFO: 0,
  IDENTITY_VERIFICATION: 1,
  CONTRACTOR_INFO: 2,
  SECURITY: 3
} as const;

const stepTitles = [
  'Crear cuenta',
];

// Interface for temporary code data
interface TemporaryCodeData {
  id: string;
  code: string;
  company: {
    id: string;
    name: string;
  };
  role: UserRole;
  description?: string;
  maxUses: number;
  currentUses: number;
  usesRemaining: number;
  isActive: boolean;
  batchId?: string;
  preloadedData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    cedula?: string;
    ordenPatronal?: string;
    polizaINS?: string;
  };
  invitationStatus?: {
    isInvitationSent: boolean;
    invitationSentAt?: string;
    invitationClicks?: number;
  };
  status: {
    isExpired: boolean;
    isExhausted: boolean;
    isAvailable: boolean;
  };
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}

// Hook for consistent error handling
const useErrorHandler = () => {
  const handleApiError = React.useCallback((error: any, context: string, showToUser: boolean = true) => {

    if (!showToUser) return null;

    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      const errorDetails = data?.message || data?.error || 'Error del servidor';
      return `Error ${status}: ${errorDetails}`;
    } else if (error.request) {
      return 'Error de conexión: No se pudo conectar con el servidor';
    } else {
      return `Error inesperado: ${error.message || 'Error desconocido'}`;
    }
  }, []);

  return { handleApiError };
};

// Format backend errors in a readable way
const formatBackendErrors = (errors: any): string => {
  if (typeof errors === 'string') {
    return errors;
  }

  if (Array.isArray(errors)) {
    return errors.map(error => {
      if (typeof error === 'string') return error;
      if (error.message) return error.message;
      if (error.field && error.message) return `${error.field}: ${error.message}`;
      return JSON.stringify(error);
    }).join('\n');
  }

  if (typeof errors === 'object' && errors !== null) {
    return Object.entries(errors).map(([field, message]) => {
      const fieldTranslations: { [key: string]: string } = {
        'cedula': 'Cédula/Identificación',
        'identification': 'Cédula/Identificación',
        'email': 'Correo Electrónico',
        'phone': 'Teléfono',
        'firstName': 'Nombre',
        'lastName': 'Apellidos',
        'password': 'Contraseña',
        'invitationCode': 'Código de Invitación'
      };

      const translatedField = fieldTranslations[field] || field;
      return `${translatedField}: ${message}`;
    }).join('\n');
  }

  return 'Error desconocido del servidor';
};

// Hook for dynamic verification fields
const useVerificationFields = (formData: StepData) => {
  return React.useMemo(() => {
    const fields = [];

    // Always show selfie first
    fields.push({
      name: 'selfie',
      label: 'Selfie',
      type: 'file',
      required: true,
      accept: 'image/*',
      capture: 'user',
      placeholder: 'Tomar selfie',
      helperText: 'Tome una foto de su rostro mirando a la cámara'
    });

    // Only show front document if selfie exists
    if (formData.selfie) {
      fields.push({
        name: 'idFront',
        label: 'Documento de Identidad (Frente)',
        type: 'file',
        required: true,
        accept: 'image/*',
        capture: 'environment',
        placeholder: 'Foto del frente del documento',
        helperText: 'Tome una foto clara del frente de su documento'
      });
    }

    // Only show back document if front exists
    if (formData.selfie && formData.idFront) {
      fields.push({
        name: 'idBack',
        label: 'Documento de Identidad (Reverso)',
        type: 'file',
        required: false,
        accept: 'image/*',
        capture: 'environment',
        placeholder: 'Foto del reverso del documento (opcional)',
        helperText: 'Si su documento tiene información en el reverso, tome una foto'
      });
    }

    return fields;
  }, [formData.selfie, formData.idFront]);
};

export const RegisterWithInvitation: React.FC = () => {
  const navigate = useNavigate();
  const strings = useStrings();
  const { code } = useParams<{ code: string }>();

  const [sessionId, setSessionId] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [tempCodeData, setTempCodeData] = useState<TemporaryCodeData | null>(null);
  // Resolved at runtime from the invitation code (see useEffect below).
  // Defaulting to CLIENT_STAFF avoids carrying a role string the BE rejects;
  // the actual role assignment happens server-side at completeRegistration
  // based on the temporary code's payload.
  const [selectedRole, setSelectedRole] = useState<UserRole>(UserRole.CLIENT_STAFF);

  // Safety check for currentStep
  const safeCurrentStep = isNaN(currentStep) ? 0 : currentStep;

  // Ref for form container for auto scroll
  const formContainerRef = React.useRef<HTMLDivElement>(null);
  // Guard against double-init. The init `useEffect` below depends on
  // `loadMockStep` (which itself depends on `currentStep`), so the
  // effect re-fires every time the user advances a step. Without this
  // ref, that re-fire calls `startRegistrationWithCode` again,
  // discards the in-flight session, and visually loops the user back
  // to step 1 — the exact "click next, nothing happens" bug.
  const initStartedForCodeRef = useRef<string | null>(null);
  // Auto scroll to last captured field
  const scrollToLastCapturedField = useCallback((fieldName: string) => {
    setTimeout(() => {
      const fieldElement = document.querySelector(`[data-field-name="${fieldName}"]`);
      if (fieldElement && formContainerRef.current) {
        fieldElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 300);
  }, []);

  // Load mock step with pre-filled data from invitation code
  const loadMockStep = useCallback(async (stepIndex?: number) => {
    if (!loading) {
      setLoading(true);
      setLoadingStartTime(Date.now());
      setMinLoadingTime(true);
    }

    const activeStep = stepIndex !== undefined ? stepIndex : currentStep;

    // Un código creado con "Crear Código" (invitación individual) no trae
    // `preloadedData`: nadie escribió la identidad del invitado por él. En ese
    // caso los campos de identidad tienen que ser EDITABLES — con
    // `disabled: true` fijo quedaban vacíos y bloqueados, y la pantalla no
    // podía completar ningún registro. Sólo se bloquean cuando efectivamente
    // vienen pre-cargados (invitación masiva), que es el caso para el que se
    // diseñó esta pantalla: confirmar datos + elegir contraseña.
    const identityPrefilled = !!tempCodeData?.preloadedData;

    // Pre-fill with data from invitation code if available
    const prefilledData = tempCodeData?.preloadedData ? {
      firstName: tempCodeData.preloadedData.firstName || '',
      lastName: tempCodeData.preloadedData.lastName || '',
      email: tempCodeData.preloadedData.email || '',
      phone: tempCodeData.preloadedData.phone || '',
      identification: tempCodeData.preloadedData.cedula || '',
      ordenPatronal: tempCodeData.preloadedData.ordenPatronal || '',
      polizaINS: tempCodeData.preloadedData.polizaINS || '',
      invitationCode: code || '',
      companyId: tempCodeData.company.id,
      batchId: tempCodeData.batchId || '',
      registrationSource: RegistrationSource.INVITATION_CODE,
      password: '',
      confirmPassword: '',
      acceptTerms: false,
      acceptPrivacyPolicy: false
    } : {
      invitationCode: code || '',
      companyId: tempCodeData?.company.id || '',
      batchId: tempCodeData?.batchId || '',
      registrationSource: RegistrationSource.INVITATION_CODE,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      identification: '',
      ordenPatronal: '',
      polizaINS: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
      acceptPrivacyPolicy: false
    };

    // Only use pre-filled data if formData is empty
    setFormData(prev => {
      if (Object.keys(prev).length === 0) {
        return prefilledData;
      } else {
        return prev;
      }
    });

    const mockSteps = [
      // Step 1: Confirm + Password (single screen).
      //
      // The invitee sees their identity data (set by the inviter) as
      // read-only confirmation fields, then enters their password +
      // accepts terms. handleNext drives the BE through steps 1, 3 (file
      // skip), 4 (contractor skip), 5 (security) in one click.
      {
        step: STEP_INDICES.PERSONAL_INFO + 1,
        title: 'Crear cuenta',
        fields: [
          {
            name: 'firstName',
            label: 'Nombre',
            type: 'text',
            required: true,
            placeholder: 'Nombre',
            disabled: identityPrefilled,
          },
          {
            name: 'lastName',
            label: 'Apellidos',
            type: 'text',
            required: true,
            placeholder: 'Apellidos',
            disabled: identityPrefilled,
          },
          {
            name: 'email',
            label: 'Correo Electrónico',
            type: 'email',
            required: true,
            placeholder: 'correo@ejemplo.com',
            disabled: identityPrefilled,
          },
          {
            name: 'phone',
            label: 'Teléfono',
            type: 'tel',
            required: false,
            placeholder: '8888-8888',
            disabled: identityPrefilled,
          },
          {
            name: 'identification',
            label: 'Documento de Identificación',
            type: 'text',
            required: false,
            placeholder: 'No registrado',
            helperText: 'Si está vacío, podrá agregarlo más tarde desde su perfil.',
            disabled: identityPrefilled,
          },
          {
            name: 'password',
            label: 'Contraseña',
            type: 'password',
            required: true,
            placeholder: 'Cree una contraseña segura',
            helperText: 'Mínimo 8 caracteres, incluye mayúsculas, minúsculas, números y símbolos',
          },
          {
            name: 'confirmPassword',
            label: 'Confirmar Contraseña',
            type: 'password',
            required: true,
            placeholder: 'Repita la contraseña',
          },
          {
            name: 'acceptTerms',
            label: 'Acepto los términos y condiciones',
            type: 'checkbox',
            required: true,
            linkTo: '/terms',
          },
          {
            name: 'acceptPrivacyPolicy',
            label: 'Acepto la política de privacidad',
            type: 'checkbox',
            required: true,
            linkTo: '/privacy',
          },
        ],
      },
      // Step 2: Identity Verification
      {
        step: STEP_INDICES.IDENTITY_VERIFICATION + 1,
        title: 'Verificación de Identidad',
        fields: []
      },
      // Step 3: Contractor Information (pre-filled from invitation)
      {
        step: STEP_INDICES.CONTRACTOR_INFO + 1,
        title: 'Información Específica del Contratista',
        fields: [
          {
            name: 'ordenPatronal',
            label: 'Orden Patronal',
            type: 'text',
            required: false,
            placeholder: 'OP-XXXX-XXX (opcional)',
            helperText: 'Número de orden patronal asignado'
          },
          {
            name: 'polizaINS',
            label: 'Póliza INS',
            type: 'text',
            required: false,
            placeholder: 'INS-XXXXXX (opcional)',
            helperText: 'Número de póliza del INS'
          }
        ]
      },
      // Step 4: Security
      {
        step: STEP_INDICES.SECURITY + 1,
        title: 'Configuración de Seguridad',
        fields: [
          {
            name: 'password',
            label: 'Contraseña',
            type: 'password',
            required: true,
            placeholder: 'Ingresa una contraseña segura',
            helperText: 'Mínimo 8 caracteres, incluye mayúsculas, minúsculas, números y símbolos'
          },
          {
            name: 'confirmPassword',
            label: 'Confirmar Contraseña',
            type: 'password',
            required: true,
            placeholder: 'Repite la misma contraseña',
            helperText: 'Debe coincidir con la contraseña anterior'
          },
          {
            name: 'acceptTerms',
            label: 'Acepto los términos y condiciones',
            type: 'checkbox',
            required: true,
            linkTo: '/terms'
          },
          {
            name: 'acceptPrivacyPolicy',
            label: 'Acepto la política de privacidad',
            type: 'checkbox',
            required: true,
            linkTo: '/privacy'
          }
        ]
      }
    ];

    const currentMockStep = mockSteps[activeStep] || mockSteps[0];

    // For identity verification step, configure fields dynamically
    let finalStep = { ...currentMockStep };
    if (activeStep === STEP_INDICES.IDENTITY_VERIFICATION) {
      finalStep = {
        ...currentMockStep,
        fields: verificationFields
      };
    }

    // Add data property required by StepInfo type
    const stepWithData = {
      ...finalStep,
      data: formData
    };

    // Simulate a small delay for better UX
    await new Promise(resolve => setTimeout(resolve, 300));

    setStepInfo(stepWithData);

    // Ensure minimum loading time before hiding skeleton
    await ensureMinimumLoadingTime();
    setLoading(false);
  }, [currentStep, tempCodeData, code]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to ensure minimum loading time for better UX
  const ensureMinimumLoadingTime = useCallback(async () => {
    if (loadingStartTime) {
      const elapsedTime = Date.now() - loadingStartTime;
      const minimumLoadingTime = 800; // 800ms minimum loading time

      if (elapsedTime < minimumLoadingTime) {
        const remainingTime = minimumLoadingTime - elapsedTime;
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      setLoadingStartTime(null);
      setMinLoadingTime(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to determine skeleton field count based on step
  const getSkeletonFieldCount = () => {
    const step = safeCurrentStep;
    switch(step) {
      case STEP_INDICES.PERSONAL_INFO: return 5;
      case STEP_INDICES.IDENTITY_VERIFICATION: return 3;
      case STEP_INDICES.CONTRACTOR_INFO: return 2;
      case STEP_INDICES.SECURITY: return 4;
      default: return 3;
    }
  };

  // State variables
  const [stepInfo, setStepInfo] = useState<StepInfo | null>(null);
  const [formData, setFormData] = useState<StepData>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [successMessages, setSuccessMessages] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);

  // Custom hooks
  const verificationFields = useVerificationFields(formData);
  const { handleApiError } = useErrorHandler();

  const [generalError, setGeneralError] = useState('');
  const [companies, setCompanies] = useState<any[]>([]);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [, setMinLoadingTime] = useState(true);
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [invitationLoading, setInvitationLoading] = useState(true);
  const [invitationError, setInvitationError] = useState('');

  // Set page title
  usePageTitle(getPageTitle('Registro con Invitación'), 'Sistema de Gestión de Seguridad - Registro con código de invitación');

  // Load invitation code data on component mount
  useEffect(() => {
    const loadInvitationData = async () => {
      if (!code) {
        setInvitationError('Código de invitación no encontrado en la URL');
        setInvitationLoading(false);
        return;
      }

      try {
        setInvitationLoading(true);

        // Get temporary code details with full data
        const result = await temporaryCodesApi.validateCode(code, true);

        if (!result.success || !result.data) {
          throw new Error(result.message || 'Código de invitación inválido');
        }

        setTempCodeData(result.data);
        setSelectedRole(result.data.role);

        // Set company data for form
        if (result.data.company) {
          setCompanies([{
            id: result.data.company.id,
            name: result.data.company.name
          }]);
        }

      } catch (error: unknown) {
        // apiClient throws the raw ApiError object (not an Error instance)
        // for non-OK responses, so `error instanceof Error` was always
        // false here and silently discarded the backend's specific reason
        // (deleted/deactivated/expired/exhausted — see temporaryCodeController.validateCode)
        // in favor of this generic fallback, which never matched the
        // keyword checks below.
        const errorMessage = error instanceof Error
          ? error.message
          : (error as { error?: string })?.error || 'Error al cargar el código de invitación';

        // If code doesn't exist or is invalid, redirect to normal registration
        if (errorMessage.includes('inválido') || errorMessage.includes('invalid') ||
            errorMessage.includes('no encontrado') || errorMessage.includes('not found') ||
            errorMessage.includes('no está disponible') || errorMessage.includes('not available') ||
            errorMessage.includes('ha expirado') || errorMessage.includes('expired')) {
          navigate('/login', {
            state: {
              message: `Código de invitación "${code}" no válido. Por favor contacte a su administrador.`,
              fromInvalidInvitation: true
            }
          });
          return;
        }

        setInvitationError(errorMessage);
      } finally {
        setInvitationLoading(false);
      }
    };

    loadInvitationData();
     
  }, [code, navigate]);

  // Start registration after invitation data is loaded
  useEffect(() => {
    if (tempCodeData && !invitationLoading && !invitationError) {
      // One-shot guard keyed on the invitation code (or 'no-code' for
      // generic flows). React-strict-mode double-effects + dep-list
      // churn from `loadMockStep`/`ensureMinimumLoadingTime` would
      // otherwise re-run this every step transition.
      const guardKey = code || 'no-code';
      if (initStartedForCodeRef.current === guardKey) return;
      initStartedForCodeRef.current = guardKey;

      const initializeRegistration = async () => {
        try {
          setLoading(true);
          setLoadingStartTime(Date.now());
          setMinLoadingTime(true);
          setCurrentStep(0);

          // Try to start session with backend. When an invitation
          // code is present, hit the invitation-aware endpoint so the
          // BE persists `invitationCode` on the session and the
          // step-2 (code-entry) step is auto-skipped on submit.
          // Otherwise the generic start creates a non-invitation
          // session, the BE returns "Código de Invitación" as step 2
          // after submitting step 1, the FE doesn't render that step,
          // and the page silently loops on a fresh session.
          try {
            const session = code
              ? await registrationService.startRegistrationWithCode(code)
              : await registrationService.startRegistration(selectedRole);
            setSessionId(session.sessionId);

            // Seed formData directly from the BE's preloadedData. The
            // public `validateCode` endpoint strips preloadedData for
            // security, so this authenticated session-start response is
            // the only place the FE gets the inviter's identity values.
            // Without this seed, loadMockStep falls back to empty strings
            // and the auto-advance-past-Personal-Info effect never fires.
            if (session.preloadedData) {
              const p = session.preloadedData;
              setFormData((prev) => ({
                ...prev,
                firstName: prev.firstName || p.firstName || '',
                lastName: prev.lastName || p.lastName || '',
                email: prev.email || p.email || '',
                phone: prev.phone || p.phone || '',
                identification: prev.identification || p.cedula || '',
                ordenPatronal: prev.ordenPatronal || p.ordenPatronal || '',
                polizaINS: prev.polizaINS || p.polizaINS || '',
                invitationCode: code || '',
                companyId: tempCodeData?.company?.id || '',
                registrationSource: RegistrationSource.INVITATION_CODE,
                acceptTerms: prev.acceptTerms || false,
                acceptPrivacyPolicy: prev.acceptPrivacyPolicy || false,
                password: prev.password || '',
                confirmPassword: prev.confirmPassword || '',
              }));
            }

            // Use mock steps for invitation registration
            await loadMockStep(0);
          } catch (apiError) {
            // Fallback: use mock data while backend is unavailable
            const mockSessionId = 'mock-session-' + Date.now();
            setSessionId(mockSessionId);

            await loadMockStep(0);
          }
        } catch (error: unknown) {
          setGeneralError('Error al iniciar el registro');
        } finally {
          await ensureMinimumLoadingTime();
          setLoading(false);
        }
      };

      initializeRegistration();
    }
  }, [code, tempCodeData, invitationLoading, invitationError, selectedRole, loadMockStep, ensureMinimumLoadingTime]);

  // Session management with timeout and recovery
  useEffect(() => {
    const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    const ACTIVITY_CHECK_INTERVAL = 60 * 1000; // 1 minute

    let activityCheckId: NodeJS.Timeout;
    let lastActivity = Date.now();

    const updateActivity = () => {
      lastActivity = Date.now();
      localStorage.setItem('registrationLastActivity', lastActivity.toString());
    };

    const checkSessionExpiry = async () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;

      if (timeSinceLastActivity > SESSION_TIMEOUT) {
        if (sessionId) {
          try {
            await registrationService.deleteSession(sessionId);
          } catch (error: unknown) {
          }
        }

        localStorage.removeItem('registrationSessionId');
        localStorage.removeItem('registrationLastActivity');
        localStorage.removeItem('registrationCurrentStep');

        setShowSessionExpiredModal(true);
        return;
      }
    };

    // Save sessionId and step when they change
    if (sessionId) {
      localStorage.setItem('registrationSessionId', sessionId);
      updateActivity();
    }

    if (currentStep !== null && currentStep !== undefined) {
      localStorage.setItem('registrationCurrentStep', currentStep.toString());
    }

    // Set up activity listeners
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity);
    });

    // Only set up activity listeners and checks if there's a session
    if (sessionId) {
      activityCheckId = setInterval(checkSessionExpiry, ACTIVITY_CHECK_INTERVAL);
    }

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });

      if (activityCheckId) clearInterval(activityCheckId);
    };
  }, [sessionId, currentStep]);

  // Debug effect
  useEffect(() => {
  }, [loading, stepInfo, invitationLoading, invitationError, tempCodeData, currentStep]);

  // Update dynamic verification fields
  useEffect(() => {
    if (stepInfo && currentStep === STEP_INDICES.IDENTITY_VERIFICATION) {

      const updatedStepInfo = {
        ...stepInfo,
        fields: verificationFields
      };
      setStepInfo(updatedStepInfo);
    }
  }, [verificationFields, currentStep, stepInfo?.step, formData.idFront, formData.selfie, stepInfo]);

  const handleFieldChange = (name: string, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      return updated;
    });
    setErrors(prev => ({ ...prev, [name]: '' }));
    setSuccessMessages(prev => ({ ...prev, [name]: '' }));

    // Real-time validation for passwords
    if (name === 'password' || name === 'confirmPassword') {
      validatePasswords(name, value);
    }
  };

  const analyzeEmail = async (email: string) => {
    if (!email || !email.includes('@')) return;

    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setErrors(prev => ({ ...prev, email: 'Formato de email inválido' }));
        return;
      }

      // `verifyEmail` deliberately answers wrong ~1 in 5 times in production
      // (anti-enumeration measure — see authController.getMe... verifyEmail).
      // It's fine as a security control, but not reliable enough to gate or
      // even hint at "already registered" here: the real check happens
      // server-side at final submission, with an honest answer. Only the
      // rate-limit case is trustworthy (not randomized).
      const data = await registrationService.verifyEmail(email);
      if (!data.success && data.retryAfter) {
        setErrors(prev => ({
          ...prev,
          email: `${data.error} Intente nuevamente en ${Math.ceil(data?.retryAfter || 0 / 60)} minutos.`
        }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    } catch (error: unknown) {
      handleApiError(error, 'Verify Email', false);
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const validatePasswords = (fieldName: string, value: string) => {
    if (fieldName === 'password') {
      const validation = registrationService.validatePassword(value);
      if (!validation.valid) {
        setErrors(prev => ({ ...prev, password: validation.errors[0] }));
      }
    }

    if (fieldName === 'confirmPassword' || (fieldName === 'password' && formData.confirmPassword)) {
      const password = fieldName === 'password' ? value : formData.password;
      const confirmPassword = fieldName === 'confirmPassword' ? value : formData.confirmPassword;

      if (confirmPassword && password !== confirmPassword) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Las contraseñas no coinciden' }));
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  const validateCurrentStep = (): boolean => {
    if (!stepInfo || !stepInfo.fields) return false;

    const newErrors: { [key: string]: string } = {};
    let isValid = true;

    stepInfo.fields.forEach(field => {
      const value = formData[field.name];

      // Required field validation
      if (field.required && !value) {
        newErrors[field.name] = `${field.label} es requerido`;
        isValid = false;
      }

      // Specific validations
      if (field.name === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          newErrors[field.name] = 'Email inválido';
          isValid = false;
        }
      }

      if (field.name === 'identification' && value) {
        const cleanValue = value.trim();

        if (!/^[A-Za-z0-9\s-]+$/.test(cleanValue)) {
          newErrors[field.name] = 'Solo se permiten letras, números, espacios y guiones';
          isValid = false;
        } else {
          const digitsOnly = cleanValue.replace(/[^0-9]/g, '');

          if (digitsOnly.length < 9) {
            newErrors[field.name] = 'El documento debe contener al menos 9 dígitos';
            isValid = false;
          } else if (digitsOnly.length > 12) {
            newErrors[field.name] = 'El documento no puede exceder 12 dígitos';
            isValid = false;
          }

          if (cleanValue.length < 9) {
            newErrors[field.name] = 'El documento es demasiado corto';
            isValid = false;
          } else if (cleanValue.length > 20) {
            newErrors[field.name] = 'El documento es demasiado largo';
            isValid = false;
          }
        }
      }

      if (field.name === 'phone' && value) {
        const cleanPhone = value.replace(/[\s-()]/g, '');

        if (!cleanPhone.startsWith('+')) {
          newErrors[field.name] = 'El número debe incluir el código de país (ej: +506 para Costa Rica)';
          isValid = false;
        } else if (cleanPhone.length < 8) {
          newErrors[field.name] = 'Número de teléfono demasiado corto';
          isValid = false;
        } else if (cleanPhone.length > 20) {
          newErrors[field.name] = 'Número de teléfono demasiado largo';
          isValid = false;
        }
      }

      if (field.name === 'password' && value) {
        const validation = registrationService.validatePassword(value);
        if (!validation.valid) {
          newErrors[field.name] = validation.errors.join(', ');
          isValid = false;
        }
      }

      if (field.name === 'confirmPassword' && value !== formData.password) {
        newErrors[field.name] = 'Las contraseñas no coinciden';
        isValid = false;
      }

      if ((field.name === 'acceptTerms' || field.name === 'acceptPrivacyPolicy') && !value) {
        newErrors[field.name] = 'Debes aceptar para continuar';
        isValid = false;
      }

      // File validation
      if (field.type === 'file' && field.required && !value) {
        newErrors[field.name] = `${field.label} es requerido`;
        isValid = false;
      }

      if (field.type === 'file' && value && value.size > 5 * 1024 * 1024) {
        newErrors[field.name] = 'El archivo no debe exceder 5MB';
        isValid = false;
      }

      if (field.type === 'file' && value && !value.type.startsWith('image/')) {
        newErrors[field.name] = 'Solo se permiten archivos de imagen';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;

    try {
      setLoading(true);
      setGeneralError('');

      // Prepare data according to current step
      const prepareStepData = () => {
        const safeCurrentStep = isNaN(currentStep) ? 0 : currentStep;

        switch (safeCurrentStep) {
          case STEP_INDICES.PERSONAL_INFO:
            return {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              cedula: formData.identification,
              temporaryCode: code,
              batchId: formData.batchId,
              registrationSource: formData.registrationSource
            };
          case STEP_INDICES.IDENTITY_VERIFICATION: {
            const verificationFormData = new FormData();
            verificationFormData.append('sessionId', sessionId);

            if (formData.selfie) {
              verificationFormData.append('selfie', formData.selfie, 'selfie.jpg');
            }
            if (formData.idFront) {
              verificationFormData.append('idFront', formData.idFront, 'documento_frente.jpg');
            }
            if (formData.idBack) {
              verificationFormData.append('idBack', formData.idBack, 'documento_reverso.jpg');
            }

            return verificationFormData;
          }
          case STEP_INDICES.CONTRACTOR_INFO:
            return {
              ordenPatronal: formData.ordenPatronal || '',
              polizaINS: formData.polizaINS || ''
            };
          case STEP_INDICES.SECURITY:
            return {
              password: formData.password,
              confirmPassword: formData.confirmPassword,
              acceptTerms: formData.acceptTerms,
              acceptPrivacyPolicy: formData.acceptPrivacyPolicy
            };
          default:
            return {};
        }
      };

      try {
        // Submit the security (BE step 5) payload. Extracted so the resume
        // path below and the end of the chain can share it.
        const submitSecurity = () => registrationService.submitStep(sessionId, {
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          acceptTerms: formData.acceptTerms,
          acceptPrivacyPolicy: formData.acceptPrivacyPolicy,
        });

        const surfaceErrors = (errs: any, fallback: string) => {
          if (errs) {
            if (typeof errs === 'object' && !Array.isArray(errs)) {
              setErrors(errs);
            } else {
              setGeneralError(formatBackendErrors(errs));
            }
          } else {
            setGeneralError(fallback);
          }
        };

        // Probe BE session state before chaining. The single-screen
        // invitation flow drives the BE through 4 sequential step submits
        // in one click. If a previous attempt advanced the session to BE
        // step 5 and then failed at completion (terms not checked, password
        // policy, duplicate cedula, etc.), the BE rolls back `completed`
        // but leaves `currentStep` at 5. Without this probe a retry would
        // post step-1 data into the step-5 validator and surface a
        // confusing "Debe aceptar términos" — the user already checked the
        // box, but the request never carried it.
        if (safeCurrentStep === STEP_INDICES.PERSONAL_INFO) {
          let beStep = 1;
          try {
            const info = await registrationService.getCurrentStep(sessionId);
            beStep = (info as any)?.currentStep ?? (info as any)?.step ?? 1;
          } catch { /* fall through to chain-from-1 */ }
          if (beStep >= 5) {
            const r = await submitSecurity();
            if (!r.success) {
              surfaceErrors((r as any).errors, r.message || (r as any).error || 'Error al completar el registro.');
              return;
            }
            setRegistrationSuccess(true);
            return;
          }
        }

        const stepData = prepareStepData();

        const response = await registrationService.submitStep(sessionId, stepData);

        if (response.success) {
          // After Personal Info success, drive the BE through the
          // remaining intermediate steps (Identity / Contractor) with
          // empty payloads, then submit Security with the password
          // payload — all in one click. The invitee never sees the
          // intermediate screens.
          if (safeCurrentStep === STEP_INDICES.PERSONAL_INFO) {
            // Step 2 (Identity Verification) — empty FormData
            const idForm = new FormData();
            idForm.append('sessionId', sessionId);
            let r = await registrationService.submitStep(sessionId, idForm);
            if (!r.success) {
              setGeneralError(r.message || 'Error al avanzar el registro.');
              return;
            }
            // Step 3 (Contractor Info) — empty plain object
            r = await registrationService.submitStep(sessionId, {
              ordenPatronal: '',
              polizaINS: '',
            });
            if (!r.success) {
              setGeneralError(r.message || 'Error al avanzar el registro.');
              return;
            }
            // Step 4 (Security) — the password + terms payload
            r = await submitSecurity();
            if (!r.success) {
              surfaceErrors((r as any).errors, r.message || (r as any).error || 'Error al completar el registro.');
              return;
            }
            setRegistrationSuccess(true);
          } else {
            setRegistrationSuccess(true);
          }
        } else {
          if (response.errors) {
            if (typeof response.errors === 'object' && !Array.isArray(response.errors)) {
              setErrors(response.errors);
            } else {
              const formattedError = formatBackendErrors(response.errors);
              setGeneralError(formattedError);
            }
          } else {
            const errorMessage = response.message || 'Error al procesar el paso';
            setGeneralError(`Error en el paso ${safeCurrentStep + 1}: ${errorMessage}`);
          }
        }
      } catch (apiError: any) {
        const errorMessage = handleApiError(apiError, 'Submit Step', true);
        setGeneralError(errorMessage || 'Error al procesar el paso');
      }
    } catch (error: unknown) {
      setGeneralError('Error al procesar el paso');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = async () => {
    if (currentStep <= 0) return;

    try {
      setLoading(true);
      setGeneralError('');

      try {
        let response = await registrationService.previousStep(sessionId);
        // Skip backwards through the auto-hidden steps so a "Back"
        // click from Seguridad lands on Información Personal rather
        // than briefly on the now-invisible Datos del Contratista or
        // Verificación de Identidad screens.
        while (
          response.success &&
          (response.currentStep - 1 === STEP_INDICES.CONTRACTOR_INFO ||
            response.currentStep - 1 === STEP_INDICES.IDENTITY_VERIFICATION)
        ) {
          response = await registrationService.previousStep(sessionId);
        }

        if (response.success) {
          const prevStep = response.currentStep - 1;
          setCurrentStep(prevStep);
          await loadMockStep(prevStep);
        } else {
          setGeneralError('No se pudo regresar al paso anterior');
        }
      } catch (apiError: any) {
        handleApiError(apiError, 'Previous Step', false);

        let prevStep = Math.max(0, currentStep - 1);
        while (
          prevStep === STEP_INDICES.CONTRACTOR_INFO ||
          prevStep === STEP_INDICES.IDENTITY_VERIFICATION
        ) {
          prevStep -= 1;
          if (prevStep < 0) {
            prevStep = 0;
            break;
          }
        }
        setCurrentStep(prevStep);
        await loadMockStep(prevStep);
      }
    } catch (error: unknown) {
      setGeneralError('Error al regresar al paso anterior');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBackToHome = async () => {
    if (sessionId) {
      try {
        await registrationService.deleteSession(sessionId);
      } catch (error: unknown) {
      }
    }

    localStorage.removeItem('registrationSessionId');
    localStorage.removeItem('registrationLastActivity');
    localStorage.removeItem('registrationCurrentStep');

    navigate('/');
  };

  const handleRefreshModalClose = async () => {
    setShowRefreshModal(false);
    // Clear the one-shot init guard so the init useEffect can re-run
    // if `tempCodeData` ever changes after this restart. Otherwise a
    // stale guard would silently block a legitimate re-init.
    initStartedForCodeRef.current = null;
    setCurrentStep(0);
    setSessionId('');
    setFormData({});
    setErrors({});
    setGeneralError('');
    setRegistrationSuccess(false);

    try {
      // Restart registration from beginning
      setLoading(true);
      setLoadingStartTime(Date.now());
      setMinLoadingTime(true);

      // Same invitation-aware short-circuit as the initial init —
      // ensures the refresh-modal restart preserves the invitation
      // context instead of dropping back into a generic flow.
      const session = code
        ? await registrationService.startRegistrationWithCode(code)
        : await registrationService.startRegistration(selectedRole);
      setSessionId(session.sessionId);

      const stepInfo = await registrationService.getCurrentStep(session.sessionId);
      setStepInfo(stepInfo);
      setCurrentStep(stepInfo.step - 1);
    } catch (error: unknown) {
      navigate(`/register/invitation/${code}`);
    } finally {
      await ensureMinimumLoadingTime();
      setLoading(false);
    }
  };

  const handleSessionExpiredModalClose = async () => {
    setShowSessionExpiredModal(false);

    localStorage.removeItem('registrationSessionId');
    localStorage.removeItem('registrationLastActivity');
    localStorage.removeItem('registrationCurrentStep');

    navigate('/login', {
      state: {
        message: 'Tu sesión de registro ha expirado por inactividad. Por favor inicia sesión o comienza un nuevo registro.',
        redirectReason: 'session_expired'
      }
    });
  };

  // Show error if invitation loading failed
  if (invitationError) {
    return (
      <InvitationErrorScreen
        errorMessage={invitationError}
        onGoToRegister={() => navigate('/register')}
        onGoToLogin={() => navigate('/login')}
      />
    );
  }

  // Show loading while invitation data is being fetched
  if (invitationLoading) {
    return (
      <InvitationLoadingScreen code={code} />
    );
  }

  // Success screen
  if (registrationSuccess) {
    return (
      <InvitationSuccessScreen
        companyName={tempCodeData?.company.name}
        role={tempCodeData?.role}
        stringsLabel={strings.sedes}
        onGoToLogin={() => navigate('/login', {
          state: {
            message: 'Registro completado exitosamente con código de invitación. Por favor inicia sesión.',
            email: formData.email
          }
        })}
      />
    );
  }

  // Early loading state
  if (!stepInfo && loading) {
    return (
      <Container component="main" maxWidth="md">
        <Box sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Multi-step form
  return (
    <>
      <style>
        {`
          @keyframes progress-skeleton {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}
      </style>
      <Container component="main" maxWidth="md">
        <Box sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          py: { xs: 2, md: 3 }
        }}>
          <Card sx={{
            width: '100%',
            position: 'relative',
            pb: { xs: 10, md: 4 },
            boxShadow: { xs: 0, md: 1 },
            border: { xs: 'none', md: '1px solid' },
            borderColor: { xs: 'transparent', md: 'divider' },
            overflow: 'hidden',
            minHeight: { xs: 'calc(100dvh - 160px)', md: '600px' }
          }}>
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                right: 20,
                opacity: 0.08,
                zIndex: 0,
                pointerEvents: 'none',
                display: { xs: 'none', md: 'block' }
              }}
            >
              <img
                src="/safety-logo.png"
                alt=""
                style={{
                  width: '100px',
                  height: '100px',
                  objectFit: 'contain'
                }}
              />
            </Box>

            <CardContent ref={formContainerRef} sx={{ p: { xs: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 3,
                mx: { xs: -3, md: -4 },
                mt: { xs: -3, md: -4 },
                px: { xs: 3, md: 4 },
                py: 3,
                backgroundColor: (theme) => theme.palette.background.primary,
                borderRadius: '0px 0 0 0'
              }}>
                <IconButton
                  onClick={handleGoBackToHome}
                  size="small"
                  sx={{ mr: 2, color: 'white' }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: '1.5rem', md: '2.125rem' },
                      color: 'white',
                      fontWeight: 600
                    }}
                  >
                    Registro con Invitación
                  </Typography>
                  {tempCodeData && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255,255,255,0.8)',
                        fontSize: '0.875rem'
                      }}
                    >
                      {tempCodeData.company.name} • Código: {code}
                    </Typography>
                  )}
                </Box>
              </Box>

            {loading && (
              <RegistrationSkeleton
                fieldCount={getSkeletonFieldCount()}
                currentStep={currentStep}
              />
            )}

            {!loading && stepInfo && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  {stepInfo.title}
                </Typography>

                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: (stepInfo.fields?.length || 0) === 1 ? '1fr' :
                        (stepInfo.fields?.length || 0) === 2 ? 'repeat(2, 1fr)' :
                        'repeat(auto-fit, minmax(280px, 1fr))'
                  },
                  gap: { xs: 2, md: 3 },
                  mb: 4,
                  minHeight: {
                    xs: (stepInfo.fields?.length || 2) * 80 + 'px',
                    md: Math.ceil((stepInfo.fields?.length || 2) / 2) * 80 + 'px'
                  }
                }}>
                  {stepInfo.fields && stepInfo.fields.map((field) => (
                    <Box
                      key={field.name}
                      sx={{ minHeight: '72px', gridColumn: field.type === 'checkbox' ? '1 / -1' : undefined }}
                      data-field-name={field.name}
                    >
                      <RegistrationField
                        field={field}
                        value={formData[field.name] || ''}
                        onChange={handleFieldChange}
                        onBlurEmail={analyzeEmail}
                        error={errors[field.name]}
                        successMessage={successMessages[field.name]}
                        companies={companies}
                        onScrollToField={scrollToLastCapturedField}
                      />
                    </Box>
                  ))}
                </Box>

                {generalError && (
                  isSafetyStaffNotAssignedError(undefined, generalError) ? (
                    <Alert severity="warning" sx={{ mb: 3, display: { xs: 'none', md: 'block' } }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                        Tu empresa todavía no está lista para nuevos usuarios
                      </Typography>
                      <Typography variant="body2">{generalError}</Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error" sx={{ mb: 3, display: { xs: 'none', md: 'block' } }}>
                      <Box component="pre" sx={{
                        fontFamily: 'inherit',
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {generalError}
                      </Box>
                    </Alert>
                  )
                )}

                {/* Desktop Buttons */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'space-between' }}>
                  {currentStep > 0 && (
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      disabled={loading}
                      startIcon={<ArrowBackIcon />}
                    >
                      Anterior
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={
                      loading ||
                      // On the final Seguridad step, require both
                      // terms + privacy checkboxes to be ticked before
                      // the submit goes through. Mirrors the BE's own
                      // validation but gives the user immediate
                      // feedback rather than a 400.
                      (currentStep === STEP_INDICES.SECURITY &&
                        (!formData.acceptTerms || !formData.acceptPrivacyPolicy))
                    }
                    endIcon={loading ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
                    fullWidth={currentStep === 0}
                  >
                    {currentStep === stepTitles.length - 1 ? 'Completar Registro' : 'Siguiente'}
                  </Button>
                </Box>
              </>
            )}
          </CardContent>

          {/* Mobile Buttons - Bottom fixed */}
          {((!loading && stepInfo) || loading) && (
            <MobileNavigationButtons
              currentStep={currentStep}
              totalSteps={stepTitles.length}
              loading={loading}
              generalError={generalError}
              onBack={handleBack}
              onNext={handleNext}
              nextDisabled={
                currentStep === STEP_INDICES.SECURITY &&
                (!formData.acceptTerms || !formData.acceptPrivacyPolicy)
              }
            />
          )}
        </Card>
      </Box>
    </Container>

      {/* Progress bar removed — the invitation flow is now a single
          screen (Crear cuenta) so there's no meaningful progress to
          show. */}

      {/* Session Refresh Modal */}
      <RefreshDetectedModal
        open={showRefreshModal}
        onStartOver={handleRefreshModalClose}
      />

      {/* Session Expired Modal */}
      <SessionExpiredModal
        open={showSessionExpiredModal}
        onGoHome={handleSessionExpiredModalClose}
      />
    </>
  );
};
